import React, { useState, useEffect } from "react";
import MenuTreeEditor from "../components/MenuTreeEditor";
import { FaCog, FaDatabase, FaEye, FaPlus, FaSave } from "react-icons/fa";
import { Modal, Button, Form, Toast, ToastContainer } from "react-bootstrap";

// Helper to always hit same-origin API
const api = (path, options = {}) =>
  fetch(`/api${path}`, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    ...options,
  });

const generateId = () => Math.random().toString(36).slice(2, 10);

export default function HeaderManager() {
  const [appId, setAppId] = useState(
    new URLSearchParams(window.location.search).get("appId") || "vik-app-v1"
  );
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);

  // Modals State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Form State
  const [editItem, setEditItem] = useState(null); // For Add/Edit
  const [appTitle, setAppTitle] = useState("");

  // Toast State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    loadConfig();
  }, [appId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api(`/superapp/config/${encodeURIComponent(appId)}`);
      if (res.ok) {
        const json = await res.json();
        const config = json?.config || {};
        setCfg(config);
        setAppTitle(config?.header?.title || "Untitled App");

        // Normalize menu items
        const items = Array.isArray(config?.header?.primaryMenu?.items)
          ? config.header.primaryMenu.items
          : Array.isArray(config?.menu)
          ? config.menu
          : [];
        setMenuItems(items);
      }
    } catch (e) {
      console.error("Failed to load config:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (nextConfig) => {
    try {
      // Helper to stringify functions
      const stringifyFunctions = (obj) => {
        if (obj === null || obj === undefined) return obj;
        if (
          typeof obj === "object" &&
          obj._functionString &&
          Object.keys(obj).length === 1
        )
          return obj;
        if (typeof obj === "function")
          return { _functionString: obj.toString() };
        if (Array.isArray(obj)) return obj.map(stringifyFunctions);
        if (typeof obj === "object") {
          const result = {};
          for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              result[key] = stringifyFunctions(obj[key]);
            }
          }
          return result;
        }
        return obj;
      };

      const prepared = stringifyFunctions(nextConfig);

      const res = await api(`/superapp/config/${encodeURIComponent(appId)}`, {
        method: "PUT",
        body: JSON.stringify({ config: prepared }),
      });

      if (res.ok) {
        const updated = await res.json();
        setCfg(updated?.config || nextConfig);
        window.dispatchEvent(
          new CustomEvent("superapp-config-updated", { detail: { appId } })
        );
        setToastMessage("Configuration saved successfully!");
        setShowToast(true);
      } else {
        throw new Error("Failed to save config");
      }
    } catch (e) {
      console.error("Save failed:", e);
      alert("Failed to save configuration");
    }
  };

  const handleMenuChange = async (newItems) => {
    setMenuItems(newItems);
    const next = {
      ...cfg,
      header: {
        ...(cfg?.header || {}),
        primaryMenu: { items: newItems },
      },
    };
    await saveConfig(next);
  };

  // --- Add Item Logic ---
  const openAddModal = () => {
    setEditItem({ label: "", path: "", visible: true });
    setShowAddModal(true);
  };

  const handleAddItem = async () => {
    if (!editItem.label) return;

    const key = editItem.label.trim().toLowerCase().replace(/\s+/g, "-");
    const newItem = {
      id: `${key}-${generateId()}`,
      key,
      label: editItem.label.trim(),
      path: editItem.path || `/${key}`,
      visible: editItem.visible,
      order: menuItems.length,
    };

    const nextItems = [...menuItems, newItem];
    await handleMenuChange(nextItems);
    setShowAddModal(false);
  };

  // --- Edit Item Logic ---
  const handleNodeClick = (item) => {
    setEditItem({ ...item });
    setShowEditModal(true);
  };

  const handleUpdateItem = async () => {
    if (!editItem || !editItem.id) return;

    const updateRecursive = (items) => {
      return items.map((item) => {
        if (item.id === editItem.id) {
          return { ...item, ...editItem };
        }
        if (item.children) {
          return { ...item, children: updateRecursive(item.children) };
        }
        return item;
      });
    };

    const nextItems = updateRecursive(menuItems);
    await handleMenuChange(nextItems);
    setShowEditModal(false);
  };

  // --- App Identity Logic ---
  const handleSaveAppIdentity = async () => {
    const next = {
      ...cfg,
      header: {
        ...(cfg?.header || {}),
        title: appTitle,
      },
    };
    await saveConfig(next);
    setShowSettingsModal(false);
  };

  return (
    <div className="header-manager-container">
      <ToastContainer position="top-end" className="p-3">
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="header-manager-content">
        <div className="header-manager-top">
          <div className="app-identity">
            <h1
              onClick={() => setShowSettingsModal(true)}
              title="Click to edit App Title"
            >
              {appTitle} <FaCog size={16} className="edit-icon" />
            </h1>
            <span className="subtitle">Header Builder</span>
          </div>
          <div className="actions">
            <Button variant="primary" onClick={openAddModal}>
              <FaPlus /> Add Header Item
            </Button>
          </div>
        </div>

        <div className="menu-tree-wrapper">
          <MenuTreeEditor
            items={menuItems}
            onChange={handleMenuChange}
            onNodeClick={handleNodeClick}
          />
        </div>
      </div>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Header Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Home"
                value={editItem?.label || ""}
                onChange={(e) =>
                  setEditItem({ ...editItem, label: e.target.value })
                }
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Path / URL</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. /home"
                value={editItem?.path || ""}
                onChange={(e) =>
                  setEditItem({ ...editItem, path: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Visible"
                checked={editItem?.visible !== false}
                onChange={(e) =>
                  setEditItem({ ...editItem, visible: e.target.checked })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddItem}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Header Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                value={editItem?.label || ""}
                onChange={(e) =>
                  setEditItem({ ...editItem, label: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Path / URL</Form.Label>
              <Form.Control
                type="text"
                value={editItem?.path || ""}
                onChange={(e) =>
                  setEditItem({ ...editItem, path: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Visible"
                checked={editItem?.visible !== false}
                onChange={(e) =>
                  setEditItem({ ...editItem, visible: e.target.checked })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateItem}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* App Settings Modal */}
      <Modal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>App Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>App Title</Form.Label>
            <Form.Control
              type="text"
              value={appTitle}
              onChange={(e) => setAppTitle(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSettingsModal(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAppIdentity}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .header-manager-container {
            display: flex;
            justify-content: center;
            padding: 40px;
            min-height: 100vh;
            background: var(--bg-color);
            color: var(--text-color);
        }
        .header-manager-content {
            width: 100%;
            max-width: 800px;
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 12px;
            padding: 30px;
            display: flex;
            flex-direction: column;
            gap: 20px;
            height: fit-content;
            min-height: 500px;
        }
        .header-manager-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 20px;
            border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .app-identity h1 {
            font-size: 1.5rem;
            margin: 0;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .app-identity .subtitle {
            font-size: 0.9rem;
            opacity: 0.6;
        }
        .edit-icon {
            opacity: 0.5;
            font-size: 0.6em;
        }
        .app-identity h1:hover .edit-icon {
            opacity: 1;
        }
        .menu-tree-wrapper {
            flex: 1;
            background: rgba(0,0,0,0.2);
            border-radius: 8px;
            padding: 15px;
            min-height: 300px;
        }

        /* Menu Tree Styles */
        .menu-tree-container {
            width: 100%;
        }
        .menu-tree-node {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 15px;
            margin-bottom: 8px;
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.05);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
            color: #eee;
            width: 100%;
        }
        .menu-tree-node:hover {
            background: rgba(255,255,255,0.1);
            border-color: rgba(255,255,255,0.1);
        }
        .menu-tree-drag {
            margin-right: 12px;
            cursor: grab;
            opacity: 0.5;
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }
        .menu-tree-drag:hover {
            opacity: 1;
        }
        .menu-tree-toggle {
            background: none;
            border: none;
            color: rgba(255,255,255,0.7);
            cursor: pointer;
            padding: 0;
            margin-right: 8px;
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }
        .menu-tree-toggle:hover {
            color: #fff;
        }
        .menu-tree-label {
            flex: 1;
            min-width: 0;
            font-size: 1rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-right: 10px;
        }
        .menu-tree-spacer {
            display: none;
        }
        .menu-tree-btn {
            background: none;
            border: none;
            color: rgba(255,255,255,0.5);
            cursor: pointer;
            padding: 6px;
            margin-left: 6px;
            display: flex;
            align-items: center;
            transition: color 0.2s;
            flex-shrink: 0;
        }
        .menu-tree-btn:hover {
            color: #fff;
        }
        .menu-tree-badge {
            background: var(--primary-color, #0d6efd);
            color: #fff;
            font-size: 0.7em;
            padding: 2px 6px;
            border-radius: 10px;
            margin-left: 8px;
            flex-shrink: 0;
        }
        .menu-tree-disabled {
            font-size: 0.7em;
            color: #ffc107;
            border: 1px solid #ffc107;
            padding: 1px 4px;
            border-radius: 4px;
            margin-left: 8px;
            flex-shrink: 0;
        }
        .menu-tree-node-disabled .menu-tree-label {
            opacity: 0.5;
            text-decoration: line-through;
        }

        /* Dark Modal Styles */
        .modal-content {
            background-color: #1e1e1e;
            color: #fff;
            border: 1px solid rgba(255,255,255,0.1);
        }
        .modal-header {
            border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        .modal-footer {
            border-top: 1px solid rgba(255,255,255,0.1);
        }
        .btn-close {
            filter: invert(1) grayscale(100%) brightness(200%);
        }
        .form-control {
            background-color: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.1);
            color: #fff;
        }
        .form-control:focus {
            background-color: rgba(255,255,255,0.1);
            border-color: var(--primary-color, #0d6efd);
            color: #fff;
            box-shadow: none;
        }
        .form-check-input {
            background-color: rgba(255,255,255,0.1);
            border-color: rgba(255,255,255,0.3);
        }
        .form-check-input:checked {
            background-color: var(--primary-color, #0d6efd);
            border-color: var(--primary-color, #0d6efd);
        }
      `}</style>
    </div>
  );
}
